import React, { useEffect, useMemo, useState } from "react";
import AdminRequestsTable from "./AdminRequestsTable";
import AdministrativeTransactionRequest from "../../../interfaces/adminRequests/AdministrativeTransactionRequest";
import { useAuth } from "../../../firebase/AuthProvider";
import { Form, Nav, Tab } from "react-bootstrap";
import { approveAdministrativeTransactionRequest, getAdministrativeTransactionRequestsForUser, rejectAdministrativeTransactionRequest } from "../../../services/AdministrativeRequestServices/AdministrativeRequestService";
import { formatDateToWords } from "../../../utilities/dateUtils";

const AdminRequests: React.FC = () => {
    const [transactions, setTransactions] = useState<AdministrativeTransactionRequest[] | null>(null);
    const { currentUser, currentOrganization } = useAuth();
    const [view, setView] = useState<string>("all"); // local state for tab view
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchTransactions();
    }, [currentUser]);

    const fetchTransactions = async () => {
        try {
            if (currentUser) {
                const authToken = await currentUser.getIdToken();
                const fetchedTransactions =
                    await getAdministrativeTransactionRequestsForUser(
                        authToken,
                        currentUser.uid
                    );

                if (fetchedTransactions) {
                    setTransactions(fetchedTransactions);
                }
            }
        } catch (err) {
            console.error(err);
        }
    };

    const normalizedSearch = useMemo(() => debouncedSearchTerm.trim().toLowerCase(), [debouncedSearchTerm]);

    const matchesSearch = useMemo(() => {
        if (!normalizedSearch) {
            return () => true;
        }

        return (tx: AdministrativeTransactionRequest) => {
            const requestedDate = tx.requestTime ? new Date(tx.requestTime) : null;
            const requestedDateStrings = requestedDate
                ? [
                    tx.requestTime,
                    requestedDate.toLocaleDateString(),
                    requestedDate.toLocaleString(),
                    formatDateToWords(requestedDate),
                ]
                : [tx.requestTime];

            const haystack = [
                tx.requesterName,
                tx.approverName ?? "",
                tx.transactionType,
                tx.status,
                tx.taskTitle ?? "",
                tx.taskId != null ? String(tx.taskId) : "",
                ...requestedDateStrings,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            return haystack.includes(normalizedSearch);
        };
    }, [normalizedSearch]);

    const filteredAllTransactions = useMemo(() => {
        if (!transactions) return null;
        return transactions.filter(matchesSearch);
    }, [transactions, matchesSearch]);

    const filteredOpenTransactions = useMemo(() => {
        if (!transactions) return null;
        return transactions
            .filter((tx) => (tx.status ?? "").toLowerCase() === "pending")
            .filter(matchesSearch);
    }, [transactions, matchesSearch]);

    const triggerTaskApproval = async (requestId: number) => {
        if (currentUser) {
            const authToken = await currentUser.getIdToken();
            const result = await approveAdministrativeTransactionRequest(
                authToken,
                requestId
            );
            if (result.success) {
                await fetchTransactions();
            } else {
                console.error("Failed to approve request.");
            }
        }
    };

    const triggerTaskRejection = async (requestId: number) => {
        if (currentUser) {
            const authToken = await currentUser.getIdToken();
            const result = await rejectAdministrativeTransactionRequest(
                authToken,
                requestId
            );
            if (result.success) {
                await fetchTransactions();
            } else {
                console.error("Failed to reject request.");
            }
        }
    };

    if (!transactions || transactions.length === 0) {
        return <div>No transactions available.</div>;
    }

    return (
        <div className="common_card">
            <div className="common_content_inner mby_t_24 mby_b_32 mb-4">
                <Form.Group>
                    <Form.Control
                        type="text"
                        placeholder="Search requests..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </Form.Group>
            </div>
            <Tab.Container id="left-tabs-example" activeKey={view} onSelect={key => setView(key || "all")}> 
                <Nav variant="pills" className="common_tab">
                    <Nav.Item>
                        <Nav.Link eventKey="open">Open</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="all">All</Nav.Link>
                    </Nav.Item>
                </Nav>
                <Tab.Content>
                    <Tab.Pane eventKey="open">
                        {currentUser && currentOrganization && (
                            <div>
                                <AdminRequestsTable
                                    transactions={(filteredOpenTransactions ?? []).filter((tx) => (tx.status ?? "").toLowerCase() === "pending")}
                                    organizationName={currentOrganization.abbreviation}
                                    approveTask={triggerTaskApproval}
                                    rejectTask={triggerTaskRejection}
                                />
                            </div>
                        )}
                    </Tab.Pane>
                    <Tab.Pane eventKey="all">
                        {currentUser && currentOrganization && (
                            <div>
                                <AdminRequestsTable
                                    transactions={filteredAllTransactions ?? []}
                                    organizationName={currentOrganization.abbreviation}
                                    approveTask={triggerTaskApproval}
                                    rejectTask={triggerTaskRejection}
                                />
                            </div>
                        )}
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </div>
    );
};

export default AdminRequests;
